import * as stylex from "@stylexjs/stylex";
import { Check, Copy, Plus, Trash2, X } from "lucide-react";
import { copy } from "@/copy";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, radii, shadows, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import type { ApiKeyRow as Row } from "../apiKeyRow";
export type SavedApiKey = {
  tail: string;
  used: string;
};
export type ApiKeyRowProps = {
  host: string;
  online: boolean;
  saved: SavedApiKey | null | undefined;
  row: Row;
  onCreate: () => void;
  onCopy: () => void;
  onDone: () => void;
  onAsk: () => void;
  onCancel: () => void;
  onRevoke: () => void;
};
const text = copy.settings.apiKey;
const styles = {
  ring: {
    kGVxlE: "xnfxhx2",
    $$css: true
  },
  done: {
    kSGwAc: "xqcrz7y",
    $$css: true
  }
};
export function ApiKeyRow({
  host,
  online,
  saved,
  row,
  onCreate,
  onCopy,
  onDone,
  onAsk,
  onCancel,
  onRevoke
}: ApiKeyRowProps) {
  const showCreate = saved === null && (row.kind === "idle" || row.kind === "creating");
  return <div className="x78zum5 xdt5ytf xddi9bj xqp6ajr x1a11ngh">
      <div className="x78zum5 x1a02dak x1qughib x6s0dn4 x190un1r">
        <span className="x78zum5 xdt5ytf xt4j9g6 xfb3i0g xk30mkq">
          <span className="x1iig92y xhxcbl2 x1hrmo45">{text.title}</span>
          <span className="x14c3fy2 x1dld7ex x1fzhlzt">{text.body}</span>
          <span className="xr0iwhn xif65rj xkv8mnm xwn7fz2 xj0a0fe">{text.endpoint(host)}</span>
        </span>
        {showCreate && <Button variant="secondary" icon={Plus} disabled={!online || row.kind === "creating"} onClick={onCreate} xstyle={styles.ring}>
            {text.create}
          </Button>}
        {saved && row.kind !== "shown" && <SavedKey saved={saved} online={online} asking={row.kind === "asking"} onAsk={onAsk} />}
      </div>
      {!online && row.kind === "idle" && <span className="xr0iwhn xif65rj xkv8mnm xwn7fz2 xj0a0fe xtvhhri">{text.offline}</span>}
      {row.kind === "asking" && <RevokeQuestion onCancel={onCancel} onRevoke={onRevoke} />}
      {row.kind === "shown" && <ShownKey apiKey={row.key} copied={row.copied} onCopy={onCopy} onDone={onDone} />}
    </div>;
}
function SavedKey({
  saved,
  online,
  asking,
  onAsk
}: {
  saved: SavedApiKey;
  online: boolean;
  asking: boolean;
  onAsk: () => void;
}) {
  return <div className="x78zum5 x1a02dak x6s0dn4 xd18lh1">
      <span className="xr0iwhn xif65rj xkv8mnm xwn7fz2 xj0a0fe">
        <span>{text.masked(saved.tail)}</span>{" "}
        <span className="xtvhhri">{saved.used}</span>
      </span>
      {!asking && <Button variant="secondary" icon={Trash2} disabled={!online} onClick={onAsk} xstyle={styles.ring}>
          {text.revoke}
        </Button>}
    </div>;
}
function RevokeQuestion({
  onCancel,
  onRevoke
}: {
  onCancel: () => void;
  onRevoke: () => void;
}) {
  return <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x1hu4l6k xq79u0e x1fv5t7x x5r8ks9">
      <span className="x1iv9x3n x14c3fy2 x1dld7ex x37zpob x1fzhlzt">{text.ask}</span>
      <div className="x78zum5 x1a02dak x152posx">
        <Button variant="ghost" onClick={onCancel}>
          {text.cancel}
        </Button>
        <Button variant="primary" icon={Trash2} onClick={onRevoke}>
          {text.confirm}
        </Button>
      </div>
    </div>;
}
function ShownKey({
  apiKey,
  copied,
  onCopy,
  onDone
}: {
  apiKey: string;
  copied: boolean;
  onCopy: () => void;
  onDone: () => void;
}) {
  return <div role="status" className="x78zum5 xdt5ytf x1hu4l6k x1qhigcl x1fv5t7x x1ubcl0s">
      <span className="x1iig92y xhxcbl2 x1hrmo45">{text.shownTitle}</span>
      <span className="x14c3fy2 x1dld7ex xfrs9s4 x1fzhlzt">{text.shownBody}</span>
      <div className="x78zum5 x1a02dak x6s0dn4 x1hu4l6k xiqviz9 x1fv5t7x xh540kj">
        <span className="x1iv9x3n xeuugli xr0iwhn x1jvydc1 x1p4m5qa x37zpob xj0a0fe xvrbanz">{apiKey}</span>
        <Button variant="secondary" icon={copied ? Check : Copy} onClick={onCopy} xstyle={styles.ring}>
          {copied ? text.copied : text.copy}
        </Button>
      </div>
      <Button variant="ghost" icon={X} onClick={onDone} xstyle={styles.done}>
        {text.done}
      </Button>
    </div>;
}